import {SLIDE_HEIGHT, SLIDE_WIDTH} from 'powtoon-commons/models/consts'
import {isDecimalInteger} from './numbers'
import {roundToSupportedPrecision} from 'powtoon-document/utils/duration'
import {MINIMUM_STEP_SIZE} from 'powtoon-document/consts/duration'
import {
  Keyframe,
  KeyframeValues,
  Camera,
  AspectRatio,
  AnimationGraphs,
  EmptyObject
} from 'powtoon-versioning/src/currentSchema'
import {CAMERA_MIN_TIME_DURATION, CAMERA_SHORT_SIDE_MIN_LIMIT, isCamera} from './camera'
import {isEmpty} from 'lodash'

export const VERTICAL_ASPECT_RATIO_WIDTH = (SLIDE_HEIGHT * SLIDE_HEIGHT) / SLIDE_WIDTH

export const ASPECT_RATIO_SIZE_MAP: {[key: string]: {x: number; width: number; multiplier: number}} = {
  none: {
    x: 0,
    width: SLIDE_WIDTH,
    multiplier: SLIDE_HEIGHT / SLIDE_WIDTH
  },
  square: {
    x: (SLIDE_WIDTH - SLIDE_HEIGHT) / 2,
    width: SLIDE_HEIGHT,
    multiplier: 1
  },
  vertical: {
    x: (SLIDE_WIDTH - VERTICAL_ASPECT_RATIO_WIDTH) / 2,
    width: VERTICAL_ASPECT_RATIO_WIDTH,
    multiplier: SLIDE_WIDTH / SLIDE_HEIGHT
  }
}

export const getAspectRatioSize = (aspectRatioMethod: AspectRatio['method'] = 'none') =>
  ASPECT_RATIO_SIZE_MAP[aspectRatioMethod] || ASPECT_RATIO_SIZE_MAP.none

export const createDefaultSlideCameraAnimationGraphs = (aspectRatioMethod?: AspectRatio['method']): AnimationGraphs =>
  ({
    keyframes: [
      {
        time: 0,
        values: createDefaultSlideCameraValues(aspectRatioMethod)
      }
    ]
  })

export const createDefaultSlideCameraValues = (aspectRatioMethod?: AspectRatio['method']) => {
  const {x, width} = getAspectRatioSize(aspectRatioMethod)
  return {
    x,
    y: 0,
    width,
    height: SLIDE_HEIGHT,
    rotation: 0
  }
}

export const isCameraFullStage = ({x, y, height}: KeyframeValues, aspectRatioMethod?: AspectRatio['method']) => {
  const aspectRatio = getAspectRatioSize(aspectRatioMethod)
  return y <= 0 && height >= SLIDE_HEIGHT && x === aspectRatio.x
}

export const isDefaultSizeCamera = (camera?: Camera | EmptyObject, aspectRatioMethod?: AspectRatio['method']) => {
  return isCamera(camera) &&
    camera?.animationGraphs?.keyframes?.length === 1 &&
    isCameraFullStage(camera.animationGraphs.keyframes[0].values, aspectRatioMethod)
}

export const isCameraValidSizeValue = ({width, height, y, x, rotation}: KeyframeValues) => (
  x >= 0 && (x + width) <= SLIDE_WIDTH &&
  y >= 0 && (y + height) <= SLIDE_HEIGHT &&
  rotation === 0
)

export const isCameraValidSizeKeyframe =
  ({values}: Keyframe) =>
    isCameraValidSizeValue(values)

export const isCameraValidSizeKeyframes =
  (keyframes: Keyframe[]) => keyframes.every(keyframe => isCameraValidSizeKeyframe(keyframe))

export const isCameraValidTimeKeyframes =
  (keyframes: Keyframe[], slideDuration: number = 1, isValidTime = isDecimalInteger) =>
    keyframes.every(({time}) => isValidTime(time) && time >= 0 && time <= slideDuration) &&
  ((keyframes.length < 2) || (keyframes[1].time - keyframes[0].time) >= MINIMUM_STEP_SIZE * 2)

export const isCameraValid = (
  camera: Camera | undefined, slideDuration: number = 1, isValidTime = isDecimalInteger
) => {
  if (!camera || isEmpty(camera)) {
    return true
  }


  return !camera.animationGraphs || (
    isCameraValidTimeKeyframes(camera.animationGraphs.keyframes, slideDuration, isValidTime) &&
      isCameraValidSizeKeyframes(camera.animationGraphs.keyframes)
  )
}

export const updateKeyframeValuesByAspectRatio =
  (values: KeyframeValues, aspectRatioMethod?: AspectRatio['method']) => {
    const aspectRatio = getAspectRatioSize(aspectRatioMethod)
    let {height, y, x} = values
    let width = Math.round(height / aspectRatio.multiplier)
    if (width > aspectRatio.width) {
      width = aspectRatio.width
    }
    if (width < CAMERA_SHORT_SIDE_MIN_LIMIT) {
      width = CAMERA_SHORT_SIDE_MIN_LIMIT
      height = Math.round(width * aspectRatio.multiplier)
    }

    if (width !== values.width) {
      const midPointX = (values.width / 2) + values.x
      x = Math.round(midPointX - (width / 2))
      if ((x + width) > SLIDE_WIDTH) {
        x = SLIDE_WIDTH - width
      } else if (x < 0) {
        x = 0
      }
    }

    if (height !== values.height) {
      const midPointY = (values.height / 2) + values.y
      y = Math.round(midPointY - (height / 2))
      if ((y + height) > SLIDE_HEIGHT) {
        y = SLIDE_HEIGHT - height
      } else if (y < 0) {
        y = 0
      }
    }

    return (width === values.width && height === values.height) ? values : {...values, x, y, width, height}
  }

export const updateSlideCameraSizeByAspectRatio = (
  camera?: Camera | EmptyObject,
  aspectRatioMethod?: AspectRatio['method']
) => {
  if (isCamera(camera)) {
    camera.animationGraphs.keyframes.forEach(keyframe => {
      const updateValues = updateKeyframeValuesByAspectRatio(keyframe.values, aspectRatioMethod)
      if (keyframe.values !== updateValues) {
        keyframe.values = updateValues
      }
    })
  }
}

export const createSlideCameraFromLastCameraKeyframe = (camera?: Camera | EmptyObject): Camera | undefined => {
  if (!isCamera(camera)) {
    return
  }
  const {length, [length - 1]: {values}} = camera.animationGraphs.keyframes
  return {
    ...camera,
    animationGraphs: {
      keyframes: [
        {
          time: 0,
          values: {...values}
        }
      ]
    }
  }
}

export const updateSlideCameraTimeByNewTotalDuration =
  (camera: Camera | undefined, currentTime: number, durationDiff: number, totalDuration: number) => {
    if (camera?.animationGraphs?.keyframes.length === 2) {
      const {animationGraphs: {keyframes: [firstKeyframe, secondKeyframe]}} = camera
      const oldStartTime = firstKeyframe.time
      const oldEndTime = secondKeyframe.time
      if ((oldEndTime >= currentTime) || (oldEndTime === totalDuration) ||
      ((currentTime - oldEndTime < 1) && (durationDiff < 0))) {

        const newTotalDuration = totalDuration + durationDiff

        let startTime = oldStartTime <= currentTime ? oldStartTime : Math.max(oldStartTime + durationDiff, 0)
        if (startTime >= newTotalDuration - CAMERA_MIN_TIME_DURATION) {
          startTime = newTotalDuration - CAMERA_MIN_TIME_DURATION
        }

        let endTime = Math.min(Math.max(oldEndTime + durationDiff, CAMERA_MIN_TIME_DURATION), newTotalDuration)
        if ((endTime - startTime) < CAMERA_MIN_TIME_DURATION) {
          endTime = startTime + CAMERA_MIN_TIME_DURATION
        }

        firstKeyframe.time = roundToSupportedPrecision(startTime)
        secondKeyframe.time = roundToSupportedPrecision(endTime)
      }
    }
  }
